import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '@/assets/images/common/kotomil-logo.png';
import searchCalendarIcon from '@/assets/images/common/search-calendar-icon.png';
import { Button } from '@/components/Button';
import clsx from 'clsx';

interface TopHeaderProps {
    showHeader: boolean;
}

function TopHeader({ showHeader }: TopHeaderProps): ReactElement {
    const { t, i18n } = useTranslation('top');
    const logoLabel = t('hero.logoLabel');
    const secondaryLabel = t('hero.secondaryLabel');
    const ctaButtonLabel = t('cta.buttonLabel');

    return (
        <header
            className={clsx(
                'glass w-full max-w-[1440px] mx-auto flex justify-between items-center px-4 md:px-6 lg:px-20 py-3 md:py-4 border-b border-white/20 shadow-sm rounded-b-2xl fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out',
                showHeader
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 -translate-y-full pointer-events-none',
            )}
        >
            <div className="flex items-center gap-x-2 md:gap-x-3">
                <img src={logo} alt="Kotomil" className="h-7 md:h-8 w-auto image-protection" />
                <div className="flex flex-row justify-center items-start leading-none">
                    <p className="text-base md:text-lg lg:text-2xl font-bold text-gray-900 tracking-wide">
                        {logoLabel}
                    </p>
                    {i18n.language?.startsWith('ja') && (
                        <p className="hidden md:flex text-[10px] lg:text-xl tracking-[0.1em] text-gray-700 font-medium ms-2 mt-0.5">
                            - {secondaryLabel}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
                <Button
                    variant="original"
                    className="h-8 md:h-8 lg:h-10 px-3 md:px-4 lg:px-6 rounded-lg text-gray-900 border-2 border-gray-900
                    flex items-center justify-center gap-1 md:gap-2 w-auto group hover:bg-white"
                    onClick={() => {
                        window.location.href = '/login';
                    }}
                >
                    <span className="text-[10px] md:text-xs lg:text-sm font-bold tracking-widest">
                        {ctaButtonLabel}
                    </span>
                    <img
                        src={searchCalendarIcon}
                        alt=""
                        className="h-3 lg:h-4 w-auto transform group-hover:rotate-10 transition-transform duration-300"
                    />
                </Button>
                <Button
                    variant="default"
                    className="hidden md:flex h-8 lg:h-10 px-4 lg:px-6 text-gray-700 bg-transparent border border-gray-900
                    transition-all duration-300 ease-in-out
                    hover:bg-kotomilThemaColor hover:border-gray-900 hover:font-bold
                    items-center justify-center w-auto"
                    onClick={() => {
                        window.location.href = '/login';
                    }}
                >
                    <span className="text-xs lg:text-sm font-medium tracking-wider">
                        {t('hero.login')}
                    </span>
                </Button>
            </div>
        </header>
    );
}

export default TopHeader;
